/**
 * D-ID Streams — real-time talking avatar via WebRTC.
 *
 * Flow:
 *   1. POST /talks/streams { source_url } → get { id, session_id, offer, ice_servers }
 *   2. Browser creates RTCPeerConnection, accepts offer, sends answer
 *   3. WebRTC video stream pipes into <video> element
 *   4. speak(text) → POST /talks/streams/{id} → D-ID generates lip-synced video on the fly
 *
 * Connection stays open across many turns — no re-handshake per message.
 */

const DID_KEY = import.meta.env.VITE_DID_KEY as string | undefined;
const DID_AVATAR =
  (import.meta.env.VITE_DID_AVATAR_URL as string | undefined) ??
  "https://gslm-web-297882549658.us-central1.run.app/avatar/teacher.png";
const DID_VOICE_PROVIDER =
  (import.meta.env.VITE_DID_VOICE_PROVIDER as string | undefined) ?? "microsoft";
const DID_VOICE_ID =
  (import.meta.env.VITE_DID_VOICE_ID as string | undefined) ?? "en-US-JennyNeural";

const API = "https://api.d-id.com";

const authHeader = () => ({
  "Content-Type": "application/json",
  Authorization: `Basic ${DID_KEY}`,
});

/** Thrown when D-ID returns 402 — trial credits exhausted or plan over quota. */
export class DIDInsufficientCreditsError extends Error {
  constructor(message = "D-ID credits exhausted") {
    super(message);
    this.name = "DIDInsufficientCreditsError";
  }
}

export class DIDStream {
  private pc: RTCPeerConnection | null = null;
  private streamId = "";
  private sessionId = "";
  private videoEl: HTMLVideoElement | null = null;
  private onSpeakStart?: () => void;
  private onSpeakEnd?: () => void;
  private speakingTimer: ReturnType<typeof setTimeout> | null = null;
  private connecting: Promise<void> | null = null;
  connected = false;

  isAvailable(): boolean {
    return !!DID_KEY;
  }

  async connect(videoEl: HTMLVideoElement, opts?: {
    sourceUrl?: string;
    onSpeakStart?: () => void;
    onSpeakEnd?: () => void;
  }): Promise<void> {
    if (!DID_KEY) throw new Error("VITE_DID_KEY not set");
    // De-duplicate concurrent connect() calls (React StrictMode mounts twice)
    if (this.connecting) return this.connecting;
    if (this.connected) return;
    this.connecting = this._doConnect(videoEl, opts);
    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  private async _doConnect(videoEl: HTMLVideoElement, opts?: {
    sourceUrl?: string;
    onSpeakStart?: () => void;
    onSpeakEnd?: () => void;
  }): Promise<void> {
    this.videoEl = videoEl;
    this.onSpeakStart = opts?.onSpeakStart;
    this.onSpeakEnd = opts?.onSpeakEnd;

    const sourceUrl = opts?.sourceUrl ?? DID_AVATAR;

    // 1. Create stream
    const createRes = await fetch(`${API}/talks/streams`, {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify({ source_url: sourceUrl, stream_warmup: true }),
    });
    if (!createRes.ok) {
      const body = await createRes.text();
      throw new Error(`D-ID create failed: ${createRes.status} ${body}`);
    }
    const data = await createRes.json() as {
      id: string;
      session_id: string;
      offer: RTCSessionDescriptionInit;
      ice_servers: RTCIceServer[];
    };
    this.streamId = data.id;
    this.sessionId = data.session_id;

    // 2. WebRTC peer connection
    const pc = new RTCPeerConnection({ iceServers: data.ice_servers });
    this.pc = pc;

    pc.ontrack = (e) => {
      if (this.videoEl && e.streams[0]) {
        this.videoEl.srcObject = e.streams[0];
        // Start muted to satisfy Chrome's autoplay policy; speak() unmutes on first call
        this.videoEl.muted = true;
        this.videoEl.play().catch((err) => console.warn("video.play() warmup:", err));
      }
    };

    pc.onicecandidate = async (e) => {
      if (!e.candidate) return;
      try {
        await fetch(`${API}/talks/streams/${this.streamId}/ice`, {
          method: "POST",
          headers: authHeader(),
          body: JSON.stringify({
            candidate: e.candidate.candidate,
            sdpMid: e.candidate.sdpMid,
            sdpMLineIndex: e.candidate.sdpMLineIndex,
            session_id: this.sessionId,
          }),
        });
      } catch (err) {
        console.warn("D-ID ICE candidate error:", err);
      }
    };

    pc.onconnectionstatechange = () => {
      console.log("D-ID connection state:", pc.connectionState);
      if (pc.connectionState === "connected") this.connected = true;
    };

    // 3. Set remote offer, send answer
    await pc.setRemoteDescription(data.offer);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    await fetch(`${API}/talks/streams/${this.streamId}/sdp`, {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify({ answer, session_id: this.sessionId }),
    });

    console.log("✅ D-ID stream connected");
  }

  /** Make the avatar speak. Audio is generated by D-ID's chosen provider. */
  async speak(text: string): Promise<void> {
    if (!this.streamId || !this.pc) {
      console.warn("D-ID not connected, cannot speak");
      return;
    }
    const clean = text.trim();
    if (!clean) return;

    // Unmute video on first speak() — speak is always called from a user
    // interaction (Send button or mic click) so autoplay policy is satisfied.
    if (this.videoEl) {
      this.videoEl.muted = false;
      this.videoEl.volume = 1.0;
      this.videoEl.play().catch((e) => console.warn("video.play() on speak:", e));
    }

    this.onSpeakStart?.();
    if (this.speakingTimer) clearTimeout(this.speakingTimer);

    try {
      const res = await fetch(`${API}/talks/streams/${this.streamId}`, {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify({
          script: {
            type: "text",
            input: clean,
            provider: {
              type: DID_VOICE_PROVIDER,
              voice_id: DID_VOICE_ID,
            },
          },
          session_id: this.sessionId,
          config: { fluent: true, pad_audio: 0.0, stitch: true },
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        console.error("D-ID speak failed:", res.status, body);
        this.onSpeakEnd?.();
        if (res.status === 402) {
          throw new DIDInsufficientCreditsError(body || "not enough credits");
        }
        return;
      }
      // Speaking end ≈ text length × 60ms/char (rough)
      const estMs = Math.max(2000, clean.length * 70);
      this.speakingTimer = setTimeout(() => this.onSpeakEnd?.(), estMs);
    } catch (e) {
      if (e instanceof DIDInsufficientCreditsError) throw e;
      console.error("D-ID speak exception:", e);
      this.onSpeakEnd?.();
    }
  }

  async disconnect(): Promise<void> {
    const id = this.streamId;
    const sid = this.sessionId;
    if (id) {
      try {
        await fetch(`${API}/talks/streams/${id}`, {
          method: "DELETE",
          headers: authHeader(),
          body: JSON.stringify({ session_id: sid }),
          keepalive: true,
        });
      } catch {
        // ignore
      }
    }
    this.pc?.close();
    this.pc = null;
    this.streamId = "";
    this.sessionId = "";
    this.connected = false;
  }

  /**
   * Fire-and-forget cleanup that works during page unload.
   * Uses sendBeacon which is the only network call guaranteed to fire
   * after the page closes / refreshes / navigates away.
   */
  disconnectBeacon(): void {
    const id = this.streamId;
    const sid = this.sessionId;
    if (!id) return;
    try {
      // DELETE via sendBeacon isn't directly supported, but we can POST
      // to a delete endpoint OR use fetch with keepalive flag.
      // D-ID requires DELETE method, so use fetch + keepalive.
      fetch(`${API}/talks/streams/${id}`, {
        method: "DELETE",
        headers: authHeader(),
        body: JSON.stringify({ session_id: sid }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // ignore
    }
    this.pc?.close();
    this.pc = null;
    this.streamId = "";
    this.sessionId = "";
    this.connected = false;
  }
}

// Register global unload handlers ONCE so streams are closed on:
//   - page close (tab closed)
//   - hard refresh
//   - navigation away
//   - mobile background (visibilitychange → hidden)
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => didStreamSingleton?.disconnectBeacon());
  window.addEventListener("beforeunload", () => didStreamSingleton?.disconnectBeacon());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      // Optional: keep alive on background tab. Comment out to allow background play.
      // didStreamSingleton?.disconnectBeacon();
    }
  });
}

let didStreamSingleton: DIDStream | null = null;

export const didStream = new DIDStream();
didStreamSingleton = didStream;
